import { LoadingState } from '@lcs/shared/api';
import { createFeatureSelector, on } from '@ngrx/store';
import { OrderingDirections, OrderingOption } from 'generated/graphql-types';
import { SystemOrAccountRegisterState } from 'src/app/common-components/register/models/register-state';
import {
   RegisterAdapter,
   createRegisterAdapter,
} from 'src/app/common-components/register/models/register-state.adapter';
import { accountIdChange } from 'src/app/store/select-account/select-account.actions';
import { SystemLogsFilterOption } from '../models/system-log-filter-option';
import { SystemLogRegisterOptions } from '../models/system-log-register-options';
import { LogRegisterListItem } from '../models/system-log-register-results.model';
import * as logRegisterActions from './system-log-register.actions';

export const systemLogRegisterFeatureKey = 'logRegister';
export interface LogRegisterState
   extends SystemOrAccountRegisterState<LogRegisterListItem, SystemLogRegisterOptions, OrderingOption> {
   filters: SystemLogsFilterOption[];
}

const adapter: RegisterAdapter<LogRegisterListItem, SystemLogRegisterOptions, OrderingOption> = createRegisterAdapter<
   LogRegisterListItem,
   SystemLogRegisterOptions,
   OrderingOption
>({
   selectId: (systemLogRegisterItem) => {
      return systemLogRegisterItem.systemLogId ?? 0;
   },
});

const initialState: LogRegisterState = {
   ...adapter.getInitialState(),
   filters: [],
   orderingOptions: [{ direction: OrderingDirections.Desc, field: 'createUserDate' }],
   isOnSystemPage: false,
};

export const logRegisterReducer = adapter.createReducer(
   logRegisterActions.logRegisterAdapterActions,
   initialState,
   on(logRegisterActions.logRegisterAdapterActions.pageEnter, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(logRegisterActions.logRegisterAdapterActions.loadSuccess, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
         endCursor: action.endCursor,
         hasNextPage: action.hasNextPage,
         systemLogs: action.items,
         totalCount: action.totalCount,
      };
   }),
   on(logRegisterActions.logRegisterInitialFilter, (state, action) => {
      return {
         ...state,
         filters: action.filters,
      };
   }),
   on(logRegisterActions.logRegisterAdapterActions.quickFilterChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         quickFilters: {
            ...state.quickFilters,
            ...action.quickFilters,
         },
      };
   }),
   on(logRegisterActions.logRegisterAdapterActions.orderingOptionsChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         orderingOptions: state.orderingOptions
            ? [
                 ...state.orderingOptions
                    .filter((option) => !action.orderingOptions.some((newOption) => newOption.field === option.field))
                    .filter((option) => option.direction !== OrderingDirections.NotSet),
                 ...action.orderingOptions,
              ]
            : action.orderingOptions,
      };
   }),
   on(logRegisterActions.setOrderingOptionsFromSessionStorage, (state, action) => {
      return {
         ...state,
         orderingOptions: action.orderingOptions,
      };
   }),
   on(logRegisterActions.logRegisterAdapterActions.clearRegisterFilters, (state) => {
      return {
         ...state,
         quickFilters: initialState.quickFilters,
         orderingOptions: initialState.orderingOptions,
      };
   }),
   on(accountIdChange, (state, action) => {
      return {
         ...state,
         quickFilters: initialState.quickFilters,
      };
   }),
   on(logRegisterActions.loadSystemLogsSuccess, (state, action) => {
      return {
         ...state,
         isOnSystemPage: true,
      };
   }),
   on(logRegisterActions.loadAccountLogsSuccess, (state, action) => {
      return {
         ...state,
         isOnSystemPage: false,
      };
   })
);
export const systemLogRegisterSelector = createFeatureSelector<LogRegisterState>(systemLogRegisterFeatureKey);
export const selectors = adapter.getSelectors(systemLogRegisterSelector);
export const {
   selectAll,
   selectCallState,
   selectTotalCount,
   selectEndCursor,
   selectQuickFilters,
   selectOrderingOptions,
} = adapter.getSelectors(systemLogRegisterSelector);
